import React, { type MouseEvent } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { Building07 } from '@flipdish/ui-icons';
import { AvatarLabelGroup } from '@flipdish/ui-library/components/atoms/Avatar';
import { Badge } from '@flipdish/ui-library/components/atoms/Badge';
import { CopyButton } from '@flipdish/ui-library/components/atoms/CopyButton';
import { getTranslate } from 'react-localize-redux';

type Props = {
  id: string;
  name: string;
  logoImageUrl?: string;
  isArchived?: boolean;
  /** Blocks selecting while another switch is in flight. */
  isDisabled?: boolean;
  /** The row was chosen; `openInNewTab` is set when the modifier key was held. */
  onSelect: (openInNewTab: boolean) => void;
  /** Accessible name of the row's button, e.g. "Switch to Tony's Pizza". */
  selectLabel: string;
  /** Accessible name of the copy button. */
  copyLabel: string;
  /** Name and announcement of the copy button once the ID is copied. */
  copiedLabel: string;
  'data-fd'?: string;
};

/**
 * One organisation in the switcher: logo, name and ID, pressable to switch, with a button that
 * copies the ID and announces the copy itself.
 */
export const OrganisationRow = ({
  id,
  name,
  logoImageUrl,
  isArchived,
  isDisabled,
  onSelect,
  selectLabel,
  copyLabel,
  copiedLabel,
  'data-fd': dataFd,
}: Props) => {
  const translate = useAppSelector((state) => getTranslate(state.locale));

  return (
    <li className="relative flex items-center justify-between gap-3" data-fd={dataFd}>
      <AvatarLabelGroup
        size="md"
        rounded={false}
        src={logoImageUrl || undefined}
        alt=""
        placeholderIcon={Building07}
        title={name}
        subtitle={
          isArchived ? (
            <span className="flex items-center gap-2">
              {id}
              <Badge size="sm" color="gray">
                {translate('Archived') as string}
              </Badge>
            </span>
          ) : (
            id
          )
        }
      />
      {/* Stretched over the row so the figure above stays valid HTML; the copy button, positioned after it, paints above it. */}
      <button
        type="button"
        aria-label={selectLabel}
        disabled={isDisabled}
        className="absolute inset-0 cursor-pointer rounded-md outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
        onClick={(event: MouseEvent<HTMLButtonElement>) => onSelect(event.metaKey || event.ctrlKey)}
      />
      <CopyButton
        value={id}
        label={copyLabel}
        copiedLabel={copiedLabel}
        className="relative"
        data-testid={dataFd ? `${dataFd}-copy` : undefined}
      />
    </li>
  );
};

export default OrganisationRow;
